import { useState } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthHeaders } from './useAuthHeaders';
import { CategoryDocument } from './useCategoryDocuments';

interface UpsertCategoryDocumentParams {
    id?: number;
    filePath: string;
    category: string;
    orderNr?: number;
}

export const useUpsertCategoryDocument = () => {
    const { settings: appSettings } = useAppSettings();
    const { getJsonHeaders } = useAuthHeaders();
    const [isUpserting, setIsUpserting] = useState(false);
    const [upsertError, setUpsertError] = useState<string | null>(null);
    const [upsertSuccess, setUpsertSuccess] = useState(false);

    const upsertCategoryDocument = async ({
        id = 0,
        filePath,
        category,
        orderNr = 0,
    }: UpsertCategoryDocumentParams): Promise<CategoryDocument | null> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsUpserting(true);
        setUpsertError(null);
        setUpsertSuccess(false);

        try {
            const payload = {
                Id: id,
                FilePath: filePath,
                Category: category,
                OrderNr: orderNr,
                LastUpdatedUtc: new Date().toISOString(),
            };

            const response = await fetch(`${appSettings.api}/UpsertCategoryDocument`, {
                method: 'POST',
                headers: getJsonHeaders(),
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upsert failed (${response.status}): ${errorText}`);
            }

            const result: CategoryDocument = await response.json();
            setUpsertSuccess(true);
            return result;
        } catch (error: any) {
            setUpsertError(error.message || 'Failed to save category document');
            throw error;
        } finally {
            setIsUpserting(false);
        }
    };

    const clearMessages = () => {
        setUpsertError(null);
        setUpsertSuccess(false);
    };

    return {
        upsertCategoryDocument,
        isUpserting,
        upsertError,
        upsertSuccess,
        clearMessages,
    };
};
